import type { BandLookup } from '@sentra/contracts';
import { passCodeSchema } from '@sentra/contracts';
import { CircleCheck, CircleX, LoaderCircle } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiRoutes } from '@/api/routes';
import { useApiErrorMessage, useErrorCodeMessage } from '@/api/use-api-error';
import { Checkbox } from '@/design/components/checkbox';
import { Input } from '@/design/components/input';
import { Label } from '@/design/components/label';
import { useApiClient } from '@/features/auth/session';
import type { MediaDevicesLike } from '@/features/persons/images/camera';
import type { DeviceStorage } from '@/features/persons/images/devices';
import { BandReader } from '@/features/scan/band-reader';
import type { LoadedDetector } from '@/features/scan/detector';
import { SCAN_OPT_OUT, SCAN_OPT_OUT_ATTRIBUTE } from '@/features/scan/wedge';

/**
 * The pre-printed band field (AD-59, AD-60), and the four things a scanned code can turn out to be.
 *
 * **It exists only in a pre-printed installation.** The caller decides that by asking the
 * configuration provider, and the field is ABSENT rather than disabled in the other mode: a disabled
 * control on the busiest screen in the product is a control somebody will eventually enable.
 *
 * **The check symbol is verified before anything is sent.** `passCodeSchema` runs the codec's own
 * modulus-37 check, so a mistyped code is refused in the browser with the message the server would
 * have used, and never becomes a database lookup.
 *
 * **A claimed band names the holder, and that is the success shape rather than an error payload.**
 * `POST /api/v1/credentials/lookup` answers `state: 'claimed'` WITH the holder's short reference
 * precisely so the operator can be told which record is already holding the band in their hand; an
 * error body has nowhere to carry it, which is why the refusal below reads the lookup's 200 rather
 * than a 409.
 *
 * **Order does not matter.** Scanning a band into an otherwise empty form and filling the form first
 * and scanning at the end are the same thing to this control: it holds a code, it checks it, and the
 * form submits it. Nothing here depends on the rest of the form being filled or empty.
 *
 * **Nothing is cached.** The lookup runs through the client directly rather than through the query
 * cache, because the key of a cached query would be a live bearer token sitting in memory for the
 * cache's lifetime, long after the registration it belonged to was finished.
 *
 * **The ways of READING a band are a sibling of this field, never a child of it.**
 * `features/scan/band-reader.tsx` renders a file input, and three end-to-end flows reach into
 * `data-testid="band-field"` for `input` and for `.text-success` under Playwright's strict mode —
 * so a second input inside that element breaks all three, several gates after the edit that caused
 * it. The field itself owns the FOCUS, because it owns the control: a decode fills it and puts the
 * caret in it, so the operator can correct or carry on without reaching for the mouse.
 */

/** What this control knows about the code it is holding. */
type BandState =
  | { readonly kind: 'empty' }
  /** Typed but not yet a well-formed code: too short, or the check symbol does not agree. */
  | { readonly kind: 'malformed' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'available' }
  | { readonly kind: 'claimed'; readonly shortRef: string }
  | { readonly kind: 'void' }
  | { readonly kind: 'unknown' }
  /** The lookup itself failed. The code may be perfectly good; the operator may retry. */
  | { readonly kind: 'failed'; readonly error: unknown };

/**
 * Whether the server has REFUSED the code in the field, which is what `aria-invalid` means.
 *
 * Deliberately not the negation of {@link useBandEntry}'s `ready`. An empty field is not ready to be
 * submitted and is not invalid either: marking a control the operator has not touched yet as invalid
 * on arrival is a refusal of nothing. The four states here are the four decisions the server
 * actually made about a code somebody typed.
 */
function bandIsRefused(state: BandState): boolean {
  return (
    state.kind === 'malformed' ||
    state.kind === 'claimed' ||
    state.kind === 'void' ||
    state.kind === 'unknown'
  );
}

/** What a caller needs from the band control to decide whether it may submit. */
export interface BandEntry {
  readonly code: string;
  readonly setCode: (next: string) => void;
  /**
   * Whether the operator has declared there is no band to scan.
   *
   * Named for what the OPERATOR said rather than for what the server then does, which
   * `passes.no_band_at_registration` decides: one of its answers mints a paper pass, another issues
   * none and has the band bound later from the record. The control and the request are identical in
   * both — a box ticked, no code sent — so this side keeps one flag and the installation owns the
   * meaning. Its predecessor was named after the minting answer, which was one of the two.
   */
  readonly noBand: boolean;
  readonly setNoBand: (next: boolean) => void;
  readonly state: BandState;
  /** Whether this entry may be submitted. */
  readonly ready: boolean;
  /** What to send, which is nothing at all on the break-glass path. */
  readonly passCode: string | undefined;
}

/**
 * The band an operator is offering, as one value the form and the record screen both read.
 *
 * **A lookup that could not run does NOT block.** The four answers the server gave — available,
 * claimed, void, unknown — are decisions and are obeyed; a transport failure is not one, and
 * refusing on it would stop a legitimate registration because the desk's network blinked, with
 * nothing on screen saying that clearing the field is the way out. Nothing is lost by letting it
 * through: the claim runs inside the create's own transaction, so an unusable band is refused there
 * by the authority rather than guessed at here.
 *
 * **An EMPTY field is not ready.** A person registered with no band in a pre-printed installation
 * resolves at no door, and the repair — binding one later — used to be unreachable. The way past it
 * is the declaration an administrator has to have armed, which sends no code at all: one answer
 * produces a pass marked as an emergency issue for the life of the record, the other produces no
 * pass and has the form require a reference code instead. Two answers, one control, because what
 * the operator is saying is the same thing either way.
 */
export function useBandEntry(active: boolean, initialCode: string | null = null): BandEntry {
  // Seeded once, from the gate's hand-off (`features/scan/handoff.ts`): an operator who scanned an
  // unclaimed blank at the door arrives here with that code already in the field. Only the initial
  // value, deliberately — the hand-off is consumed on mount, and re-seeding on a later render would
  // put a band back after the operator had cleared it.
  const [code, setCode] = useState(initialCode ?? '');
  const [noBand, setNoBand] = useState(false);
  // The lookup is short-circuited when the caller is not in a pre-printed installation, and when the
  // operator has said there is no band: asking the server about an empty string answers `empty` and
  // costs a render, and asking about a code they have abandoned answers about the wrong thing.
  const state = useBandCheck(active && !noBand ? code : '');
  const trimmed = code.trim();

  return {
    code,
    setCode,
    noBand,
    setNoBand,
    state,
    ready: !active || noBand || state.kind === 'available' || state.kind === 'failed',
    passCode: !active || noBand || trimmed.length === 0 ? undefined : trimmed,
  };
}

/** How long the field waits after the last keystroke before asking the server. */
const LOOKUP_DEBOUNCE_MS = 250;

function stateFor(lookup: BandLookup): BandState {
  switch (lookup.state) {
    case 'available': {
      return { kind: 'available' };
    }
    case 'claimed': {
      return { kind: 'claimed', shortRef: lookup.shortRef };
    }
    case 'void': {
      return { kind: 'void' };
    }
    case 'unknown': {
      return { kind: 'unknown' };
    }
  }
}

/**
 * What the server says about the code currently in the field, checked as it is typed.
 *
 * A hook rather than a callback the field calls, so the state lives where it is read and there is
 * no `onStateChange` in an effect's dependency list — which is the shape that turns one keystroke
 * into a render loop the first time a caller forgets to memoize.
 */
function useBandCheck(code: string): BandState {
  const client = useApiClient();
  const trimmed = code.trim();
  // What the server said about ONE code. Keyed by the code it answered for, so an answer about a
  // code the operator has already changed is never shown as an answer about the new one.
  const [answered, setAnswered] = useState<{ code: string; state: BandState } | null>(null);

  // The two states this process can decide by itself are DERIVED rather than stored: an empty field
  // and a code whose check symbol disagrees are both pure functions of what has been typed, and
  // storing them would mean a render that shows the previous answer before an effect corrects it.
  const decided = useMemo<BandState | null>(() => {
    if (trimmed.length === 0) {
      return { kind: 'empty' };
    }
    return passCodeSchema.safeParse(trimmed).success ? null : { kind: 'malformed' };
  }, [trimmed]);

  useEffect(() => {
    if (decided !== null) {
      return undefined;
    }
    let live = true;
    const timer = setTimeout(() => {
      client
        .request(apiRoutes.credentialLookup, { body: { code: trimmed } })
        .then((lookup) => {
          if (live) {
            setAnswered({ code: trimmed, state: stateFor(lookup) });
          }
        })
        .catch((error: unknown) => {
          if (live) {
            setAnswered({ code: trimmed, state: { kind: 'failed', error } });
          }
        });
    }, LOOKUP_DEBOUNCE_MS);

    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [client, decided, trimmed]);

  if (decided !== null) {
    return decided;
  }
  return answered?.code === trimmed ? answered.state : { kind: 'checking' };
}

export function BandField({
  entry,
  required = false,
  offerNoBand = false,
  noBandLabel,
  noBandHint,
  disabled = false,
  reader,
}: Readonly<{
  entry: BandEntry;
  /**
   * Whether a submission has been attempted with nothing usable in the field.
   *
   * The refusal is shown on the attempt rather than as the operator types, for the reason
   * `aria-invalid` skips `empty`: a control nobody has touched is not a control anybody got wrong.
   */
  required?: boolean;
  /**
   * Whether to offer the declaration that there is no band, which an administrator has to have armed.
   *
   * A boolean plus its two strings rather than the setting's own value, so this control never learns
   * which of the two answers the installation chose: the box, the request and the readiness rule are
   * identical either way, and the caller is where the sentences differ.
   */
  offerNoBand?: boolean;
  /** What the box says. Required whenever {@link offerNoBand} is set. */
  noBandLabel?: string;
  /** What sits under it. Required whenever {@link offerNoBand} is set. */
  noBandHint?: string;
  /**
   * Whether the screen around this field has gone inert, which the registration form does the
   * moment a record is created.
   *
   * The reader is then not RENDERED, rather than rendered disabled. A `<fieldset disabled>` stops
   * a button being pressed and does nothing at all to a `<video>` that is already streaming, so a
   * registration committed with the band camera open would leave the camera lamp on over a form
   * nobody can use. Unmounting is what stops the tracks, in `useCamera`'s own cleanup.
   */
  disabled?: boolean;
  /** The camera and decoder seams, none of which exist in `jsdom`. Defaulted inside the reader. */
  reader?: Readonly<{
    media?: MediaDevicesLike | null;
    storage?: DeviceStorage;
    loadDetector?: () => Promise<LoadedDetector>;
  }>;
}>): ReactNode {
  const { t } = useTranslation();
  const describe = useApiErrorMessage();
  const describeCode = useErrorCodeMessage();
  const controlId = useId();
  const control = useRef<HTMLInputElement | null>(null);
  const noBandId = `${controlId}-no-band`;
  const statusId = `${controlId}-status`;
  const state = entry.state;
  const missing = required && !entry.ready && state.kind === 'empty';

  // `setCode` is `useState`'s own setter and never changes, so this callback is stable across
  // renders — which keeps the decode loop inside `ScanCamera` from being torn down and re-armed
  // on every keystroke the lookup provokes.
  const { setCode } = entry;
  const read = useCallback(
    (code: string) => {
      setCode(code);
      control.current?.focus();
    },
    [setCode],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5" data-testid="band-field">
        <Label htmlFor={controlId}>{t('person.form.bandLabel')}</Label>
        <Input
          id={controlId}
          ref={control}
          autoComplete="off"
          className="font-mono"
          // AD-32: this is a dedicated code field, so a scan into it is taken directly rather
          // than intercepted and replayed — exactly as the gate's own field declares.
          {...{ [SCAN_OPT_OUT_ATTRIBUTE]: SCAN_OPT_OUT }}
          // `checking` is not invalid: marking the field so while the lookup is in flight flashes a
          // refusal at an operator who has typed a perfectly good code.
          aria-invalid={state.kind !== 'checking' && bandIsRefused(state)}
          aria-describedby={statusId}
          disabled={entry.noBand}
          value={entry.code}
          onChange={(event) => {
            entry.setCode(event.target.value.toUpperCase());
          }}
        />
        <p id={statusId} role="status" className="flex items-center gap-1.5 text-xs">
          {missing ? (
            // The SERVER's own sentence for this refusal, rendered from its code rather than written
            // a second time here: the browser refuses first so nobody walks away bandless, and the
            // two must not be able to say different things about one rule.
            <span className="text-destructive">{describeCode('CREDENTIAL_CODE_REQUIRED')}</span>
          ) : (
            <BandStatus state={state} describe={describe} />
          )}
        </p>
        {offerNoBand && (
          <label className="flex items-start gap-2 text-xs" htmlFor={noBandId}>
            <Checkbox
              id={noBandId}
              className="mt-0.5"
              checked={entry.noBand}
              onChange={(event) => {
                entry.setNoBand(event.target.checked);
                // The code goes with the choice. A band left in the field while the box is ticked is
                // one nobody would notice was ignored, and un-ticking would restore a code the
                // operator had already decided against.
                entry.setCode('');
              }}
            />
            <span className="flex flex-col gap-0.5">
              <span className="font-medium">{noBandLabel}</span>
              <span className="text-muted-foreground">{noBandHint}</span>
            </span>
          </label>
        )}
      </div>

      {/*
        The ways of reading a band, beside the field rather than inside it — see the module note.

        Absent while the break-glass is ticked, for the same reason the field above is disabled
        there: that box means there is no band, `useBandEntry` stops asking the server about one and
        sends no code at all, so a decode would drop a value into a dead field and be discarded at
        submit with nothing on screen saying why.
      */}
      {!disabled && !entry.noBand && <BandReader onCode={read} {...(reader ?? {})} />}
    </div>
  );
}

function BandStatus({
  state,
  describe,
}: Readonly<{ state: BandState; describe: (error: unknown) => string }>): ReactNode {
  const { t } = useTranslation();

  switch (state.kind) {
    case 'empty': {
      return <span className="text-muted-foreground">{t('person.form.bandHint')}</span>;
    }
    case 'malformed': {
      return <span className="text-destructive">{t('person.form.bandMalformed')}</span>;
    }
    case 'checking': {
      return (
        <>
          <LoaderCircle aria-hidden className="size-3 animate-spin" />
          <span className="text-muted-foreground">{t('common.loading')}</span>
        </>
      );
    }
    case 'available': {
      return (
        <>
          <CircleCheck aria-hidden className="size-3 text-success" />
          <span className="text-muted-foreground">{t('person.form.bandAvailable')}</span>
        </>
      );
    }
    case 'claimed': {
      return (
        <>
          <CircleX aria-hidden className="size-3 text-destructive" />
          <span className="text-destructive">
            {t('person.form.bandClaimed', { shortRef: state.shortRef })}
          </span>
        </>
      );
    }
    case 'void': {
      return <span className="text-destructive">{t('person.form.bandVoid')}</span>;
    }
    case 'unknown': {
      return <span className="text-destructive">{t('person.form.bandUnknown')}</span>;
    }
    case 'failed': {
      return <span className="text-destructive">{describe(state.error)}</span>;
    }
  }
}
